import classNames from "classnames";
import ToolTip from "components/design/toolTip";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { Info } from "components/icons/Info";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { useToast } from "hooks/useToast";
import { ReactNode } from "react";
import {
  useAddCrmSectionInstanceMutation,
  useRemoveCrmSectionInstanceMutation,
} from "store/apis/crmFields";
import ThreeDots from "static/images/ThreeDots.svg";
import { CrmField, CrmSection } from "types/Models/crmFields";
import { CRMFieldControl } from "./CRMFieldControl";
import { CRMFieldDisplay } from "./CRMFieldDisplay";

const COLUMN_CLASS: Record<number, string> = {
  1: "t-grid-cols-1",
  2: "t-grid-cols-2",
  3: "t-grid-cols-3",
};

// Deferring the modal open avoids a Radix bug where a DropdownMenu.Item opening a Dialog in
// the same tick can leave `pointer-events: none` stuck on <body> after the dropdown's
// dismiss-layer cleanup races the dialog's own open, making the whole page unclickable - same
// fix as src/components/Entity/EntityFormEngine/Jurisdictions.tsx.
const openAfterDropdownCloses = (open: () => void) => () => {
  setTimeout(open, 0);
};

const FieldLabel = ({ field }: { field: CrmField }) => (
  <span className="t-flex t-items-center t-gap-1">
    {field.label}
    {field.help_text && (
      <ToolTip text={field.help_text}>
        <span className="t-flex">
          <Info color="currentColor" size="12" />
        </span>
      </ToolTip>
    )}
  </span>
);

const ActionMenu = ({
  children,
  ariaLabel,
}: {
  children: ReactNode;
  ariaLabel: string;
}) => (
  <Dropdown.Root>
    <Dropdown.Trigger asChild>
      <div>
        <Button size="small" customType="ghost" aria-label={ariaLabel}>
          <img src={ThreeDots} alt="" className="t-select-none" />
        </Button>
      </div>
    </Dropdown.Trigger>
    <Dropdown.Portal>
      <Dropdown.Content align="end" side="bottom" className="!t-min-w-40">
        {children}
      </Dropdown.Content>
    </Dropdown.Portal>
  </Dropdown.Root>
);

const FieldGrid = ({
  fields,
  columnCount,
  isAdmin,
  section,
  onEditField,
  onRemoveField,
  onViewAuditLog,
}: {
  fields: CrmField[];
  columnCount: number;
  isAdmin: boolean;
  section: CrmSection;
  onEditField: (field: CrmField) => void;
  onRemoveField: (section: CrmSection, field: CrmField) => void;
  onViewAuditLog: (field: CrmField) => void;
}) => (
  <div
    className={classNames(
      "t-grid t-gap-6",
      COLUMN_CLASS[columnCount] || COLUMN_CLASS[2]
    )}
  >
    {fields.map((field) => (
      <div
        key={
          field.instance_id
            ? `${field.instance_id}:${field.uuid}`
            : field.placement_id
        }
        className="t-group t-flex t-items-start t-justify-between t-gap-2"
      >
        <InfoItem label={<FieldLabel field={field} />} className="t-grow">
          {field.is_read_only ? (
            <CRMFieldDisplay field={field} />
          ) : (
            <CRMFieldControl field={field} />
          )}
        </InfoItem>
        {isAdmin && (
          <div className="t-shrink-0 t-invisible group-hover:t-visible focus-within:t-visible">
            <ActionMenu ariaLabel={`${field.label} field options`}>
              <Dropdown.Item
                onSelect={openAfterDropdownCloses(() => onEditField(field))}
              >
                Edit field
              </Dropdown.Item>
              <Dropdown.Item onSelect={() => onRemoveField(section, field)}>
                Remove from section
              </Dropdown.Item>
              <Dropdown.Item
                onSelect={openAfterDropdownCloses(() => onViewAuditLog(field))}
              >
                Audit logs
              </Dropdown.Item>
            </ActionMenu>
          </div>
        )}
      </div>
    ))}
  </div>
);

/**
 * A collapsible section of the CRM layout. Values are always editable inline; the controls that
 * change the schema itself sit behind a menu so day to day use is just filling fields in.
 */
export const CRMSectionCard = ({
  section,
  entityId,
  isAdmin,
  isOpen,
  onOpenChange,
  unsavedCount,
  onAddField,
  onEditField,
  onRemoveField,
  onEditSection,
  onDeleteSection,
  onViewAuditLog,
}: {
  section: CrmSection;
  entityId: string;
  isAdmin: boolean;
  isOpen: boolean;
  onOpenChange: (shouldOpen: boolean) => void;
  unsavedCount: number;
  onAddField: (section: CrmSection) => void;
  onEditField: (field: CrmField) => void;
  onRemoveField: (section: CrmSection, field: CrmField) => void;
  onEditSection: (section: CrmSection) => void;
  onDeleteSection: (section: CrmSection) => void;
  onViewAuditLog: (field: CrmField) => void;
}) => {
  const { alertToast } = useToast();
  const [addInstance, { isLoading: isAddingInstance }] =
    useAddCrmSectionInstanceMutation();
  const [removeInstance, { isLoading: isRemovingInstance }] =
    useRemoveCrmSectionInstanceMutation();

  const onAddInstance = async () => {
    try {
      await addInstance({ entityId, sectionId: section.uuid }).unwrap();
    } catch (error: any) {
      alertToast({
        message:
          error?.data?.message || `Could not add another ${section.title}`,
      });
    }
  };

  const onRemoveInstance = async (instanceId: string) => {
    try {
      await removeInstance({ entityId, instanceId }).unwrap();
    } catch (error: any) {
      alertToast({
        message:
          error?.data?.message || `Could not remove this ${section.title}`,
      });
    }
  };

  const instances = section.instances || [];

  return (
    <Accordion.Root
      type="single"
      collapsible
      value={isOpen ? section.uuid : ""}
      onValueChange={(value: string) => onOpenChange(value === section.uuid)}
    >
      <Accordion.Item value={section.uuid}>
        <div className="t-flex t-items-center">
          <Accordion.Trigger>
            <span className="t-flex t-items-center t-gap-2 t-normal-case">
              <span className="t-text-subtitle">{section.title}</span>
              {section.is_repeatable && (
                <Tag tagType="gray" size="small">
                  {instances.length}
                </Tag>
              )}
              {!isOpen && unsavedCount > 0 && (
                <Tag tagType="orange" size="small">
                  {unsavedCount} unsaved
                </Tag>
              )}
            </span>
          </Accordion.Trigger>
          {isAdmin && (
            <div className="t-shrink-0 t-pr-4">
              <ActionMenu ariaLabel={`${section.title} section options`}>
                <Dropdown.Item
                  onSelect={openAfterDropdownCloses(() => onAddField(section))}
                >
                  Add field
                </Dropdown.Item>
                <Dropdown.Item
                  onSelect={openAfterDropdownCloses(() =>
                    onEditSection(section)
                  )}
                >
                  Rename section
                </Dropdown.Item>
                {!section.is_default && (
                  <Dropdown.Item onSelect={() => onDeleteSection(section)}>
                    Delete section
                  </Dropdown.Item>
                )}
              </ActionMenu>
            </div>
          )}
        </div>

        <Accordion.Content>
          {section.is_repeatable ? (
            <div className="t-flex t-flex-col t-gap-4">
              {instances.length === 0 && (
                <div className="t-text-body-sm t-text-text-30">
                  No {section.title.toLowerCase()} yet
                </div>
              )}
              {instances.map((instance, index) => (
                <div
                  key={instance.instance_id}
                  className="t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4"
                >
                  <div className="t-flex t-items-center t-justify-between t-pb-3">
                    <span className="t-text-body-sm t-text-text-30">
                      {section.title} {index + 1}
                    </span>
                    {instances.length > 1 && (
                      <Button
                        size="small"
                        customType="ghost"
                        type="button"
                        isLoading={isRemovingInstance}
                        disabled={isRemovingInstance}
                        onClick={() => onRemoveInstance(instance.instance_id)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <FieldGrid
                    fields={instance.fields}
                    columnCount={section.column_count}
                    isAdmin={isAdmin}
                    section={section}
                    onEditField={onEditField}
                    onRemoveField={onRemoveField}
                    onViewAuditLog={onViewAuditLog}
                  />
                </div>
              ))}
              <div>
                <Button
                  size="small"
                  customType="ghost"
                  type="button"
                  isLoading={isAddingInstance}
                  disabled={isAddingInstance}
                  onClick={onAddInstance}
                >
                  Add another {section.title.toLowerCase()}
                </Button>
              </div>
            </div>
          ) : section.fields.length === 0 ? (
            <div className="t-text-body-sm t-text-text-30">No fields yet</div>
          ) : (
            <FieldGrid
              fields={section.fields}
              columnCount={section.column_count}
              isAdmin={isAdmin}
              section={section}
              onEditField={onEditField}
              onRemoveField={onRemoveField}
              onViewAuditLog={onViewAuditLog}
            />
          )}
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};
